import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { ReactComponent as SortFilterIcon } from '../assets/icons/sort-filter.svg';
import { ReactComponent as SearchIcon } from '../assets/icons/search.svg';
import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import EditIcon from '@mui/icons-material/Edit';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LabelIcon from '@mui/icons-material/Label';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as SidebarJobMgmtIcon } from '../assets/icons/sidebar_job_management.svg';
import PageHeader from '../components/PageHeader';
import { RankStarInline } from '../components/RankStar';
import { RANK_TIER_ORDER, RANK_TIER_LABELS } from '../constants/rankColors';

// API関連のインポート
import { jobPostingApi, storeApi, jobPositionApi, skillSettingsApi, getApiErrorMessage } from '../services/api';
import { JobPostingTemplate, JobPostingTemplateCreate, JobPositionWithIndustry, SkillTier } from '../types';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';

// JobPostingTemplateCreateを拡張した型
interface ExtendedJobPostingTemplateCreate extends JobPostingTemplateCreate {
  store_id: string | null;
  position_id?: string;
  salary?: string;
  admin_label?: string;
  template_name?: string;
  tier_code?: string;
  // サーバの必要ランクは TierObject の ID で持つ（tier_code は画面表示用のコード）。
  // null を明示送信すると「指定なし（ALL）」に戻す意味になる。
  required_tier_id?: string | null;
}

/**
 * サーバが返す required_tier（TierObject）を画面用の tier_code に展開する。
 * サーバは tier_code というキーを返さないため、これを通さないとランクの表示・編集・複製が
 * すべて空になる（動作確認NG: 2-2 No.14「複製でランクが引き継がれない」）。
 */
const withTierCode = (template: JobPostingTemplate): JobPostingTemplate => ({
  ...template,
  tier_code: template.required_tier?.code ?? template.tier_code,
});

const JobPostingTemplates: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { storeId } = useAuth(); // AuthContextから店舗IDを取得
  const { managementToken } = useManagementAuth();
  // ProtectedRoutes 配下で管理トークンは Layout の自動ログインで設定される（LineBroadcast と同パターン）
  const token = managementToken || localStorage.getItem('management_token') || '';

  const [templates, setTemplates] = useState<JobPostingTemplate[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<ExtendedJobPostingTemplateCreate | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<JobPostingTemplate | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState<boolean>(false);
  const [templateToDelete, setTemplateToDelete] = useState<JobPostingTemplate | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false); // 詳細表示用モーダルの状態
  const [, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isCopyMode, setIsCopyMode] = useState<boolean>(false); // コピーモードフラグ

  // 入力エラー箇所までスクロール＆フォーカス
  const scrollToField = (fieldName: string) => {
    setTimeout(() => {
      const el = document.querySelector(`[name="${fieldName}"]`) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
    }, 100);
  };
  
  // 追加: 店舗情報の状態
  const [storeAddress, setStoreAddress] = useState<string>("");
  const [loadingStoreInfo, setLoadingStoreInfo] = useState<boolean>(true);

  // 修正: 職種情報の状態（業種情報を含む）
  const [jobPositions, setJobPositions] = useState<JobPositionWithIndustry[]>([]);
  const [loadingJobPositions, setLoadingJobPositions] = useState<boolean>(true);

  // ランク（スキルランクティア）のマスタ。必要ランクの保存は tier_id で行うため、
  // 画面が扱う tier_code との相互変換にこのマスタが要る。
  const [skillTiers, setSkillTiers] = useState<SkillTier[]>([]);

  // tier_code → tier_id（保存用）。マスタ未取得・未知コードなら null＝指定なし
  const tierIdFromCode = useCallback((code?: string): string | null => {
    if (!code) return null;
    return skillTiers.find((t) => t.code === code)?.tier_id ?? null;
  }, [skillTiers]);

  // 並び替え用の状態
  const [sortOption, setSortOption] = useState<string>("created_at_desc");
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const openSortMenu = Boolean(sortAnchorEl);

  // ランクティアのマスタを取得（必要ランクの tier_code ⇔ tier_id 変換に使う）
  useEffect(() => {
    const fetchSkillTiers = async () => {
      if (!storeId || !token) {
        setSkillTiers([]);
        return;
      }
      try {
        const tiers = await skillSettingsApi.getSkillTiers(storeId, token);
        setSkillTiers([...tiers].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
      } catch {
        // 取得失敗時はランク指定なしとして扱う（ひな形の他項目は編集できる）
        setSkillTiers([]);
      }
    };
    fetchSkillTiers();
  }, [storeId, token]);

  // 修正: 職種情報を取得（業種情報を含む）
  useEffect(() => {
    const fetchJobPositions = async () => {
      setLoadingJobPositions(true);
      try {
        if (!!storeId) {
          // api.tsで定義した関数を使用して業種情報を含む職種情報を取得
          const positions = await jobPositionApi.getActiveWithIndustry(storeId);
          setJobPositions(positions);
        } else {
          setJobPositions([]);
        }
      } catch (err: any) {
        setJobPositions([]);
        // エラーメッセージは表示しない（職種が設定されていない場合もある）
      } finally {
        setLoadingJobPositions(false);
      }
    };

    fetchJobPositions();
  }, [storeId]);

  // 追加: 店舗情報を取得
  useEffect(() => {
    const fetchStoreInfo = async () => {
      setLoadingStoreInfo(true);
      try {
        // 店舗IDを使用して店舗情報を取得（storeIdがnullの場合も考慮）
        if (!!storeId) {
          const storeInfo = await storeApi.getStoreInfo(storeId);
          setStoreAddress(storeInfo.address);
          setError(null);
        } else {
          // 店舗IDがnull（未設定）の場合はデフォルト値を設定
          setStoreAddress("東京都渋谷区恵比寿1-1-1");
        }
      } catch (err: any) {
        setError('店舗情報の取得に失敗しました。デフォルトの勤務地を使用します。');
        // エラー時はデフォルトの場所を設定
        setStoreAddress("東京都渋谷区恵比寿1-1-1");
      } finally {
        setLoadingStoreInfo(false);
      }
    };

    fetchStoreInfo();
  }, [storeId]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      // storeIdをクエリパラメータとして渡す
      const data = await jobPostingApi.getAll({}, storeId);

      // サーバは必要ランクを required_tier（TierObject）で返す。画面はランクをコードで扱うため
      // ここで tier_code に展開する。これを行わないとランクバッジ・編集・複製が常に空になる。
      const normalized = data.map(withTierCode);

      // 取得したテンプレートの勤務地を現在の店舗勤務地で上書き
      // ただし店舗情報が取得できていない場合は元の値を維持
      if (storeAddress) {
        const updatedData = normalized.map(template => ({
          ...template,
          // 表示用に勤務地を更新（保存はされない）
          address: storeAddress
        }));
        setTemplates(updatedData);
      } else {
        setTemplates(normalized);
      }

      setError(null);
    } catch (err: any) {
      setError('ひな形の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // 店舗情報が取得できてからテンプレートを取得
    if (!loadingStoreInfo) {
      fetchTemplates();
    }
  }, [loadingStoreInfo, storeAddress, storeId]);

  // ─────────────────────────────────────────────────────────────
  // フォームの表示状態は URL で表現する
  //   ?mode=create / ?mode=edit&id=<id> / ?mode=copy&id=<id>
  //   （今すぐ募集からの旧リンク ?create=true も互換で受ける）
  //
  // 目的は2つ（動作確認NG: 2 求人作成とLINE配信 ①②）。
  //  ① フォームの初期化を「URL → フォーム」の一方向に限定する。開いているフォームを
  //     別の初期化処理（空フォームの自動オープン等）が後から上書きする余地をなくす。
  //  ② 編集画面が履歴エントリになるため、ブラウザの戻るで一覧へ戻る
  //     （従来は showForm の内部stateだけで、戻ると前の画面＝今すぐ募集まで抜けていた）。
  // ─────────────────────────────────────────────────────────────
  // 現在フォームが表しているURLキー（'create' / 'edit:<id>' / 'copy:<id>'）。
  const formKeyRef = useRef<string | null>(null);
  // このページ内の操作で履歴を積んだか（直リンク・?create=true で入った場合は false）。
  const formPushedRef = useRef(false);

  const openFormByUrl = (params: Record<string, string>) => {
    // 同じ画面を二度押し（連打）した場合に履歴が二重に積まれると
    // 戻るを2回押さないと一覧に戻れなくなるため、同一URLなら積まない
    const isSameUrl =
      Object.entries(params).every(([key, value]) => searchParams.get(key) === value) &&
      Array.from(searchParams.keys()).every((key) => key in params);
    if (isSameUrl) return;

    formPushedRef.current = true;
    setSearchParams(params); // replace 指定なし＝履歴に積む
  };

  // URLからフォーム状態が外れたとき（＝戻る操作）に呼ぶ。
  // selectedTemplate は詳細モーダルの表示にも使うためここでは触らない。
  const closeFormState = () => {
    formKeyRef.current = null;
    formPushedRef.current = false;
    setShowForm(false);
    setCurrentTemplate(null);
    setIsCopyMode(false);
    setError(null);
    setFieldErrors({});
  };

  const applyCreateForm = useCallback(() => {
    setCurrentTemplate({
      template_name: '',
      name: '',
      description: '',     // 業務内容（労働条件通知書用）
      content: '',         // 仕事内容（求人用）
      job_title: '',
      job_type: 'パート・アルバイト',
      address: storeAddress, // 店舗の場所を使用
      salary_type: 'hourly',
      salary_amount: undefined,
      salary_allowance: '',
      salary_transport: '',
      is_active: true,
      has_line_message: true, // 常にON
      line_message_template: '', // 配信メッセージ
      store_id: storeId,
      position_id: '',
      salary: '',
      admin_label: ''
    });
    
    setSelectedTemplate(null);
    setIsCopyMode(false);
    setTabValue(0);
    setError(null);
    setFieldErrors({});
    setShowForm(true);
  }, [storeAddress, storeId]);

  const applyEditForm = useCallback((template: JobPostingTemplate) => {
    setSelectedTemplate(template);

    // currentTemplateにAPIに送信するデータを設定
    setCurrentTemplate({
      template_name: template.template_name || '',
      name: template.name,
      description: template.description || '',     // 業務内容（労働条件通知書用）
      content: template.content,                   // 仕事内容（求人用）
      job_title: template.job_title,
      job_type: template.job_type,
      address: storeAddress, // APIから取得した店舗の場所を使用
      salary_type: template.salary_type || 'hourly',
      salary_amount: template.salary_amount,
      salary_allowance: template.salary_allowance || '',
      salary_transport: template.salary_transport || '',
      is_active: template.is_active,
      has_line_message: template.has_line_message,
      line_message_template: template.line_message_template || '', // 配信メッセージ
      // 追加のフィールド
      store_id: template.store_id || storeId,
      position_id: template.position_id ?? "",
      salary: template.salary || formatSalary(template),
      admin_label: template.admin_label || '',
      tier_code: template.tier_code || undefined  // ランク（未設定なら「指定なし」）
    });

    setIsCopyMode(false);
    setTabValue(0);
    setError(null);
    setFieldErrors({});
    setShowForm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeAddress, storeId]);

  // 新規追加: テンプレートをコピーして新規作成
  const applyCopyForm = useCallback((template: JobPostingTemplate) => {
    // 複製は「新規作成」として扱う（保存時に update ではなく create を通す）
    setSelectedTemplate(null);

    // currentTemplateにコピー元のデータを設定（一部プロパティを調整）
    setCurrentTemplate({
      template_name: template.template_name ? `${template.template_name} (コピー)` : '',
      // name は「募集区分」＝職種マスタの名称そのもの。ここに「(コピー)」を付けると
      // 募集区分セレクトの選択肢と一致せず空欄表示になるため、値は変えない
      // （複製の識別は「ひな形名」側の (コピー) が担う。動作確認NG: 2-2 No.14）
      name: template.name,
      description: template.description || '',     // 業務内容（労働条件通知書用）
      content: template.content,                   // 仕事内容（求人用）
      job_title: template.job_title,
      job_type: template.job_type,
      address: storeAddress, // 現在の店舗の場所を使用
      salary_type: template.salary_type || 'hourly',
      salary_amount: template.salary_amount,
      salary_allowance: template.salary_allowance || '',
      salary_transport: template.salary_transport || '',
      is_active: true, // 新規作成時は有効にしておく
      has_line_message: template.has_line_message,
      line_message_template: template.line_message_template || '', // 配信メッセージ
      // 追加のフィールド
      store_id: storeId, // 現在の店舗IDを使用
      position_id: template.position_id ?? "",
      salary: template.salary || formatSalary(template),
      admin_label: template.admin_label ? `${template.admin_label} (コピー)` : '',
      tier_code: template.tier_code || undefined  // ランクも複製する
    });

    // コピーモードをオンに
    setIsCopyMode(true);
    setTabValue(0);
    setError(null);
    setFieldErrors({});
    setShowForm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeAddress, storeId]);

  // URL（?mode=... / 旧 ?create=true）からフォームの表示・初期値を組み立てる。
  // すでに同じURLキーのフォームを開いているときは何もしない＝入力中の値を
  // 一覧の再取得や店舗情報の再取得で上書きしない（動作確認NG: 2 求人作成とLINE配信 ①）。
  useEffect(() => {
    if (loadingStoreInfo) return; // 勤務地のプリフィルに店舗情報が必要

    const legacyCreate = searchParams.get('create') === 'true';
    const mode = legacyCreate ? 'create' : (searchParams.get('mode') || '');
    const id = searchParams.get('id') || '';
    const urlKey =
      mode === 'create' ? 'create'
      : (mode === 'edit' || mode === 'copy') && id ? `${mode}:${id}`
      : null;

    if (urlKey === formKeyRef.current) return;

    // フォームを表すパラメータが無くなった＝ブラウザ戻る等。一覧に戻す。
    if (!urlKey) {
      closeFormState();
      return;
    }

    if (urlKey === 'create') {
      formKeyRef.current = urlKey;
      applyCreateForm();
      return;
    }

    // 編集・複製は元のひな形が必要。一覧の取得前なら取得後に再実行される。
    const target = templates.find((t) => String(t.id) === id);
    if (!target) return;

    formKeyRef.current = urlKey;
    if (mode === 'edit') {
      applyEditForm(target);
    } else {
      applyCopyForm(target);
    }
  }, [searchParams, loadingStoreInfo, templates, applyCreateForm, applyEditForm, applyCopyForm]);

  const handleCreateTemplate = () => {
    openFormByUrl({ mode: 'create' });
  };

  const handleEditTemplate = (template: JobPostingTemplate) => {
    // 詳細モーダルから遷移した場合、モーダルを閉じないとひな形フォームの上に残り続ける
    setIsDetailOpen(false);
    openFormByUrl({ mode: 'edit', id: String(template.id) });
  };

  const handleCopyTemplate = (template: JobPostingTemplate) => {
    // 詳細モーダルの「コピー」から呼ばれた場合、モーダルを閉じないとひな形作成画面の
    // 上にモーダルが残ったままになる（動作確認NG: 2 求人作成とLINE配信 ①）
    setIsDetailOpen(false);
    openFormByUrl({ mode: 'copy', id: String(template.id) });
  };

  // フォーム画面の「＜」。このページで積んだ履歴を1つ戻す＝一覧に戻る。
  // ブラウザの戻るも同じ位置に戻るため、両者の挙動が一致する（動作確認NG: 2 求人作成とLINE配信 ②）。
  const handleFormBack = () => {
    if (formPushedRef.current || searchParams.get('create') === 'true') {
      navigate(-1);
      return;
    }
    // 直リンク・リロードで開いた場合は戻る先が無いのでURLからパラメータだけ落とす
    setSearchParams({}, { replace: true });
  };

  // 詳細表示ハンドラ
  const handleViewDetails = (template: JobPostingTemplate) => {
    // 表示用に勤務地を現在の店舗勤務地に置き換えたテンプレートを作成
    const detailTemplate = {
      ...template,
      address: storeAddress || template.address
    };
    
    setSelectedTemplate(detailTemplate);
    setIsDetailOpen(true);
  };

  const handleDeleteTemplate = (template: JobPostingTemplate) => {
    setTemplateToDelete(template);
    setIsDeleteAlertOpen(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;
    
    setIsLoading(true);
    try {
      // storeIdをクエリパラメータとして渡す
      await jobPostingApi.delete(templateToDelete.id, storeId);
      setError(null);
      await fetchTemplates();
    } catch (err: any) {
      setError('ひな形の削除に失敗しました。');
    } finally {
      setIsLoading(false);
      setIsDeleteAlertOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleSaveTemplate = async () => {
    if (!currentTemplate) return;

    // 入力バリデーション（インラインエラー）
    const errors: Record<string, string> = {};
    if (!currentTemplate.template_name?.trim()) {
      errors.template_name = 'ひな形名を入力してください';
    }
    if (!currentTemplate.name?.trim()) {
      errors.name = '募集区分を選択してください';
    }
    if (!currentTemplate.content?.trim()) {
      errors.content = '仕事内容(求人用)は必須です';
    }
    if (
      currentTemplate.salary_amount === undefined ||
      currentTemplate.salary_amount === null ||
      Number.isNaN(currentTemplate.salary_amount) ||
      currentTemplate.salary_amount <= 0
    ) {
      errors.salary_amount = '時給を入力してください';
    }
    if (!currentTemplate.line_message_template?.trim()) {
      errors.line_message_template = '配信メッセージは必須です';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError(null);
      scrollToField(Object.keys(errors)[0]);
      return;
    }

    setFieldErrors({});
    setError(null);
    setIsLoading(true);

    try {
      // 給与テキストのフォーマット
      let formattedSalary = '';
      if (currentTemplate.salary_amount) {
        if (currentTemplate.salary_type === 'hourly') {
          formattedSalary = `時給 ${currentTemplate.salary_amount.toLocaleString()}円`;
        } else {
          formattedSalary = `日給 ${currentTemplate.salary_amount.toLocaleString()}円`;
        }
        
        if (currentTemplate.salary_allowance) {
          formattedSalary += ` / 手当: ${currentTemplate.salary_allowance}`;
        }
        
        if (currentTemplate.salary_transport) {
          formattedSalary += ` / 交通費: ${currentTemplate.salary_transport}`;
        }
      } else {
        formattedSalary = '応相談';
      }

      // 保存前に勤務地を現在表示している値に確実に上書き
      // storeAddressが空の場合に備えてフォールバック値も設定
      const AddressToSave = storeAddress || "東京都渋谷区恵比寿1-1-1";

      // バックエンドに送信するデータ
      const templateData: ExtendedJobPostingTemplateCreate = {
        template_name: currentTemplate.template_name || undefined,
        name: currentTemplate.name,
        description: currentTemplate.description,
        content: currentTemplate.content,
        job_title: currentTemplate.job_title?.trim() || currentTemplate.name,
        job_type: currentTemplate.job_type,
        address: AddressToSave, // 現在表示されている勤務地を使用
        salary_type: currentTemplate.salary_type,
        salary_amount: currentTemplate.salary_amount,
        salary_allowance: currentTemplate.salary_allowance,
        salary_transport: currentTemplate.salary_transport,
        is_active: currentTemplate.is_active,
        has_line_message: currentTemplate.has_line_message,
        line_message_template: currentTemplate.line_message_template, // 配信メッセージ
        // 追加のフィールドを明示的に設定
        store_id: storeId,
        position_id: currentTemplate.position_id || undefined,
        salary: formattedSalary,
        admin_label: currentTemplate.admin_label,
        // 必要ランクはサーバ側が tier_id で保持する。送らないと画面で選んだランクが
        // 一切保存されない（動作確認NG: 2-2 No.14）。「指定なし」は null で明示送信し、
        // 既存の必要ランクを解除できるようにする。
        // ただしランクマスタを取得できていないときは変換できないため、キー自体を送らない
        // （null を送ると設定済みの必要ランクを意図せず解除してしまう）
        ...(skillTiers.length > 0
          ? { required_tier_id: tierIdFromCode(currentTemplate.tier_code) }
          : {})
      };
      
      // 明示的に型を指定して変数を宣言
      let result: JobPostingTemplate;
      
      if (selectedTemplate && !isCopyMode) {
        // 既存テンプレートの更新 - storeIdをクエリパラメータとして渡す
        result = await jobPostingApi.update(selectedTemplate.id, templateData, storeId);
        
        // 更新されたテンプレートの勤務地を最新情報に
        const displayTemplate = {
          ...withTierCode(result),
          address: AddressToSave
        };

        setTemplates(templates.map(t =>
          t.id === result.id ? displayTemplate : t
        ));
  
        // 詳細モーダルが開いている場合は、selectedTemplateを更新
        if (isDetailOpen) {
          setSelectedTemplate(displayTemplate);
        }
      } else {
        // 新規テンプレートの作成 - storeIdをクエリパラメータとして渡す
        result = await jobPostingApi.create(templateData, storeId);
        
        // 新規テンプレートの勤務地を最新情報に
        const displayTemplate = {
          ...withTierCode(result),
          address: AddressToSave
        };

        setTemplates([...templates, displayTemplate]);
        
        // コピーモードで詳細モーダルが開いている場合は、新しく作成されたテンプレートを詳細モーダルに表示
        if (isCopyMode && isDetailOpen) {
          setSelectedTemplate(displayTemplate);
        }
      }

      // 保存後は一覧へ戻す。URLのフォームパラメータを落とすと上のuseEffectが
      // フォームを閉じる（履歴を積んでいれば戻ることで一覧の履歴位置に復帰する）
      if (formPushedRef.current) {
        navigate(-1);
      } else {
        setSearchParams({}, { replace: true });
      }

      // 詳細モーダルが開いていない場合のみselectedTemplateをnullにする
      if (!isDetailOpen) {
        setSelectedTemplate(null);
      }
  
    } catch (err: any) {
      // ユーザーに表示するエラーメッセージ（生の detail は連結しない）
      setError(getApiErrorMessage(err, 'ひな形の保存に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  };

  // 数値をカンマ区切りでフォーマットする関数
  const formatWithCommas = (value: string | number | undefined | null): string => {
    if (value === undefined || value === null || value === '') return '';
    const num = typeof value === 'number' ? value : parseInt(String(value).replace(/[^\d]/g, ''), 10);
    if (isNaN(num)) return '';
    return num.toLocaleString();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (!currentTemplate) return;

    // 入力したフィールドのエラーを解除
    if (name && fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }

    // 数値フィールドの処理
    if (name === 'salary_amount' || name === 'salary_allowance' || name === 'salary_transport') {
      // 数値以外の文字を除去（カンマも除去）
      const numericOnly = value.replace(/[^\d]/g, '');
      if (name === 'salary_amount') {
        const numberValue = numericOnly === '' ? undefined : parseInt(numericOnly, 10);
        setCurrentTemplate({
          ...currentTemplate,
          [name]: numberValue
        });
      } else {
        setCurrentTemplate({
          ...currentTemplate,
          [name]: numericOnly
        });
      }
    } else {
      setCurrentTemplate({
        ...currentTemplate,
        [name]: value
      });
    }

    // 勤務地フィールドの場合は変更を無視
    if (name === 'address') {
      return; 
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;

    // 入力したフィールドのエラーを解除
    if (name && fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }

    // 通常のAPIフィールドの処理
    if (name === 'job_type' || name === 'salary_type') {
      if (!currentTemplate) return;
      setCurrentTemplate({
        ...currentTemplate,
        [name]: value
      });
      return;
    }

    // 募集区分（name）の選択処理 - 職種名から対応するposition_idも設定
    if (name === 'name') {
      if (!currentTemplate) return;
      const selectedPosition = jobPositions.find(pos => pos.name === value);
      setCurrentTemplate({
        ...currentTemplate,
        name: value,
        position_id: selectedPosition ? selectedPosition.id.toString() : '',
        job_title: value
      });
      return;
    }

    // position_idの処理を追加
    if (name === 'position_id') {
      if (!currentTemplate) return;

      // 選択された職種のIDから職種名を取得
      const selectedPosition = jobPositions.find(position => position.id.toString() === value);
      const jobTitle = selectedPosition ? selectedPosition.name : '';

      setCurrentTemplate({
        ...currentTemplate,
        position_id: value,
        job_title: jobTitle // job_titleも同時に更新
      });
      return;
    }
  };

  const handleSwitchChange = (name: string, checked: boolean) => {
    if (!currentTemplate) return;
    
    setCurrentTemplate({
      ...currentTemplate,
      [name]: checked
    });
  };

  const handleOpenSortMenu = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleCloseSortMenu = () => {
    setSortAnchorEl(null);
  };

  const handleSortChange = (option: string) => {
    setSortOption(option);
    handleCloseSortMenu();
  };

  // テンプレートを並び替え
  const sortedTemplates = [...templates].sort((a, b) => {
    switch (sortOption) {
      case 'created_at_desc':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'created_at_asc':
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      case 'updated_at_desc':
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      case 'updated_at_asc':
        return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
      case 'name_asc':
        return a.name.localeCompare(b.name);
      case 'name_desc':
        return b.name.localeCompare(a.name);
      default:
        return 0;
    }
  });

  // 検索クエリでフィルタリング
  const filteredTemplates = sortedTemplates.filter(template =>
    (template.template_name && template.template_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.job_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (template.admin_label && template.admin_label.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // 給与情報フォーマット
  const formatSalary = (template: JobPostingTemplate): string => {
    const parts = [];
    
    if (template.salary_amount) {
      if (template.salary_type === 'hourly') {
        parts.push(`時給 ${template.salary_amount.toLocaleString()}円`);
      } else {
        parts.push(`日給 ${template.salary_amount.toLocaleString()}円`);
      }
    }
    
    if (template.salary_allowance) {
      const formattedAllowance = formatNumericValue(template.salary_allowance);
      parts.push(`手当: ${formattedAllowance}`);
    }
    
    if (template.salary_transport) {
      const formattedTransport = formatNumericValue(template.salary_transport);
      parts.push(`交通費: ${formattedTransport}`);
    }
    
    return parts.length > 0 ? parts.join(' / ') : template.salary || '応相談';
  };

  // 数字のみの場合はコンマと円を付与する関数
  const formatNumericValue = (value?: string): string => {
    if (value === undefined || value === null || value === '') {
      return '-';
    }
    const trimmedValue = value.trim();
    if (/^\d+$/.test(trimmedValue)) {
      return `${parseInt(trimmedValue, 10).toLocaleString()}円`;
    }
    return trimmedValue;
  };

  // 日時のフォーマット
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderMobileCard = (template: JobPostingTemplate) => (
    <Card key={template.id} sx={{ mb: 1.5, borderRadius: 2, boxShadow: 'none', border: '1px solid #e7e7e7' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* ステータスチップ + 編集・複製ボタン */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Chip
            label={template.is_active ? '有効' : '無効'}
            size="small"
            sx={{
              bgcolor: template.is_active ? '#d0f3ee' : '#f0f0f0',
              color: template.is_active ? '#13c3a8' : '#898989',
              fontWeight: 600,
              fontSize: '0.85rem',
              border: 'none',
              px: 1,
            }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {/* 編集ボタン */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleEditTemplate(template)}
              sx={{
                bgcolor: '#fff',
                color: '#888',
                borderColor: '#ccc',
                '&:hover': { bgcolor: '#f5f5f5', borderColor: '#aaa' },
                borderRadius: 6,
                px: 1.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                minWidth: 'auto',
                lineHeight: 1,
              }}
            >
              編集
            </Button>
            {/* 複製ボタン */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleCopyTemplate(template)}
              sx={{
                bgcolor: '#fff',
                color: '#888',
                borderColor: '#ccc',
                '&:hover': { bgcolor: '#f5f5f5', borderColor: '#aaa' },
                borderRadius: 6,
                px: 1.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                minWidth: 'auto',
                lineHeight: 1,
              }}
            >
              複製
            </Button>
          </Box>
        </Box>

        {/* 日付 */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {new Date(template.updated_at).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\//g, '.')}
        </Typography>

        {/* ひな形名 */}
        {template.template_name && (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.8rem',
              fontWeight: 500,
              color: '#666',
              mb: 0.5,
            }}
          >
            {template.template_name}
          </Typography>
        )}

        {/* タイトル */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '1rem',
            fontWeight: 600,
            color: '#3c3c3c',
            mb: 1,
            cursor: 'pointer',
            '&:hover': { color: 'primary.main' }
          }}
          onClick={() => handleViewDetails(template)}
        >
          {template.name}
        </Typography>

        {/* ランクバッジ */}
        {template.tier_code && (
          <Box sx={{ mb: 1 }}>
            <RankStarInline tierCode={template.tier_code} size={18} />
          </Box>
        )}

        {/* 募集メモ */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: '0.875rem',
            lineHeight: 1.5,
            mb: 1.5,
          }}
        >
          {template.admin_label || '募集メモなし'}
        </Typography>

        {/* 雇用形態 - ラベルと値を揃える */}
        <Box sx={{ display: 'flex', mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: '70px' }}>
            雇用形態
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {template.job_type}
          </Typography>
        </Box>

        {/* 報酬 - ラベルと値を揃える */}
        <Box sx={{ display: 'flex', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: '70px', flexShrink: 0 }}>
            報酬
          </Typography>
          <Box sx={{ flex: 1 }}>
            {template.salary_amount && (
              <Typography variant="body2" sx={{ mb: 0.3 }}>
                {template.salary_type === 'hourly' ? '時給' : '日給'}:{template.salary_amount.toLocaleString()}円
              </Typography>
            )}
            {template.salary_allowance && (
              <Typography variant="body2" sx={{ mb: 0.3 }}>
                手当:{formatNumericValue(template.salary_allowance)}
              </Typography>
            )}
            {template.salary_transport && (
              <Typography variant="body2" sx={{ mb: 0.3 }}>
                交通費:{formatNumericValue(template.salary_transport)}
              </Typography>
            )}
            {!template.salary_amount && !template.salary_allowance && !template.salary_transport && (
              <Typography variant="body2" color="text.secondary">
                応相談
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>

      {/* カードフッター：ボタン類 */}
      <CardActions sx={{
        px: 2,
        pb: 2,
        pt: 0,
        display: 'flex',
        gap: 1,
        alignItems: 'center',
      }}>
        {/* このひな形で募集するボタン */}
        <Button
          variant="contained"
          size="small"
          sx={{
            bgcolor: '#0071e3',
            fontWeight: 600,
            borderRadius: 6,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            flex: 1,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#0062c4', boxShadow: 'none' },
          }}
          component={Link}
          to={`/line-broadcast?template=${template.id}`}
        >
          このひな形で募集する
        </Button>

        {/* 削除アイコンボタン */}
        <IconButton
          onClick={() => handleDeleteTemplate(template)}
          size="small"
          sx={{
            color: '#dc2626',
            p: 0.5,
            flexShrink: 0,
          }}
        >
          <SvgIcon component={TrashIcon} inheritViewBox sx={{ fontSize: 20, color: '#dc2626' }} />
        </IconButton>
      </CardActions>
    </Card>
  );

  const renderDesktopCard = (template: JobPostingTemplate) => (
    <TableRow
      key={template.id}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
        {template.template_name && (
          <Typography variant="caption" sx={{ display: 'block', color: '#666', fontWeight: 500 }}>
            {template.template_name}
          </Typography>
        )}
        <Typography
          sx={{
            cursor: 'pointer',
            color: '#1976d2',
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={() => handleViewDetails(template)}
        >
          {template.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {formatDate(template.updated_at)}
        </Typography>
      </TableCell>
      <TableCell>{template.job_title}</TableCell>
      <TableCell>{template.admin_label || '-'}</TableCell>
      <TableCell>{template.job_type}</TableCell>
      <TableCell>{template.address || storeAddress || "勤務地情報なし"}</TableCell>
      <TableCell>{formatSalary(template)}</TableCell>
      <TableCell>
        <Chip
          label={template.is_active ? '有効' : '無効'}
          size="small"
          sx={{
            bgcolor: template.is_active ? '#d0f3ee' : '#f0f0f0',
            color: template.is_active ? '#13c3a8' : '#898989',
            fontWeight: 600,
            fontSize: '1rem',
            border: `1px solid ${template.is_active ? '#13c3a8' : '#d0d0d0'}`,
          }}
        />
      </TableCell>
      <TableCell>
        <Button
          variant="contained"
          size="small"
          sx={{ bgcolor: '#0071e3', '&:hover': { bgcolor: '#0062c4' }, mb: 1 }}
          component={Link}
          to={`/line-broadcast?template=${template.id}`}
        >
          今すぐ募集
        </Button>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton 
            aria-label="編集" 
            onClick={() => handleEditTemplate(template)}
            color="primary"
            size="small"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            aria-label="コピー"
            onClick={() => handleCopyTemplate(template)}
            color="secondary"
            size="small"
          >
            <ContentCopyIcon />
          </IconButton>
          <IconButton
            aria-label="削除"
            onClick={() => handleDeleteTemplate(template)}
            sx={{ color: '#dc2626' }}
            size="small"
          >
            <SvgIcon component={TrashIcon} inheritViewBox sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f8f9' }}>
      {!showForm && (<>
      <PageHeader
        title="求人ひな形"
        icon={<SvgIcon component={SidebarJobMgmtIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
      />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>

{error && (
  <Alert severity="error" sx={{ mb: 2 }}>
    {error}
  </Alert>
)}

{/* 新規作成ボタン（独立行・全幅・左辺強調） */}
<Button
  fullWidth
  onClick={handleCreateTemplate}
  disabled={isLoading || loadingStoreInfo || loadingJobPositions}
  sx={{
    mb: 2,
    bgcolor: '#fff',
    color: '#0071e3',
    border: '2px solid #bfdbfe',
    borderRadius: '10px',
    py: 0.5,
    fontWeight: 600,
    fontSize: '0.9rem',
    boxShadow: 'none',
    justifyContent: 'center',
    textAlign: 'center',
    '&:hover': {
      bgcolor: '#f0f7ff',
      border: '2px solid #bfdbfe',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      bgcolor: '#fff',
      color: '#aaa',
      border: '2px solid #e0e0e0',
    },
  }}
>
  新規ひな形を作成する
</Button>

<Paper sx={{ p: 0, mb: 2, bgcolor: 'transparent', border: 'none', boxShadow: 'none', borderRadius: 0 }}>
  <Box sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 1
  }}>
    <TextField
      placeholder="募集職種名や職種で検索"
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      size="small"
      sx={{
        flex: 1,
        maxWidth: { xs: '100%', sm: 300 },
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          bgcolor: '#fff',
          '& fieldset': { border: 'none' },
          '&.Mui-focused fieldset': { border: 'none' },
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SvgIcon component={SearchIcon} inheritViewBox sx={{ color: '#898989', fontSize: 20 }} />
          </InputAdornment>
        ),
      }}
    />
    <Box
      onClick={handleOpenSortMenu}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'pointer',
        color: '#898989',
        px: 0.5,
        '&:hover': { color: '#3c3c3c' },
      }}
    >
      <SvgIcon component={SortFilterIcon} inheritViewBox sx={{ fontSize: 28 }} />
      <Typography sx={{ fontSize: '0.65rem', lineHeight: 1, mt: 0.2 }}>並び替え</Typography>
    </Box>
    <Menu
      anchorEl={sortAnchorEl}
      open={openSortMenu}
      onClose={handleCloseSortMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <MenuItem onClick={() => handleSortChange('created_at_desc')} sx={{ fontSize: '0.8rem', py: 0.5, minHeight: 'auto' }}>作成日が新しい</MenuItem>
      <MenuItem onClick={() => handleSortChange('created_at_asc')} sx={{ fontSize: '0.8rem', py: 0.5, minHeight: 'auto' }}>作成日が古い</MenuItem>
      <MenuItem onClick={() => handleSortChange('updated_at_desc')} sx={{ fontSize: '0.8rem', py: 0.5, minHeight: 'auto' }}>更新日が新しい</MenuItem>
      <MenuItem onClick={() => handleSortChange('updated_at_asc')} sx={{ fontSize: '0.8rem', py: 0.5, minHeight: 'auto' }}>更新日が古い</MenuItem>
      <MenuItem onClick={() => handleSortChange('name_asc')} sx={{ fontSize: '0.8rem', py: 0.5, minHeight: 'auto' }}>名前 昇順</MenuItem>
      <MenuItem onClick={() => handleSortChange('name_desc')} sx={{ fontSize: '0.8rem', py: 0.5, minHeight: 'auto' }}>名前 降順</MenuItem>
    </Menu>
  </Box>
</Paper>

      {isLoading || loadingStoreInfo || loadingJobPositions ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {filteredTemplates.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography>ひな形がありません</Typography>
            </Box>
          ) : isMobile ? (
            // モバイル表示用のカードビュー
            <Box>
              {filteredTemplates.map((template) => renderMobileCard(template))}
            </Box>
          ) : (
            // デスクトップ表示用のテーブルビュー
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="templates table">
                <TableHead>
                  <TableRow>
                    <TableCell>募集職種名</TableCell>
                    <TableCell>職種</TableCell>
                    <TableCell>募集メモ</TableCell>
                    <TableCell>雇用形態</TableCell>
                    <TableCell>勤務地</TableCell>
                    <TableCell>給与</TableCell>
                    <TableCell>状態</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTemplates.map((template) => renderDesktopCard(template))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      </Box>{/* close px-py box */}
      </>)}{/* end !showForm list screen */}

      {showForm && (
      <Box sx={{ bgcolor: '#f8f8f9' }}>
        <PageHeader
          title="求人ひな形"
          icon={<SvgIcon component={SidebarJobMgmtIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
          onBack={handleFormBack}
        />

        <Box sx={{ p: 2, bgcolor: '#f8f8f9' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" noValidate autoComplete="off">

            {/* ひな形名 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#3c3c3c', fontSize: '0.875rem' }}>
                ひな形名
              </Typography>
              <TextField
                name="template_name"
                value={currentTemplate?.template_name || ''}
                onChange={handleInputChange}
                fullWidth
                placeholder="例: 夏季限定ホールスタッフ、週末キッチン補助など"
                error={!!fieldErrors.template_name}
                helperText={fieldErrors.template_name}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#e7e7e7' },
                    '&:hover fieldset': { borderColor: '#c4c4c4' },
                    '&.Mui-focused fieldset': { borderColor: '#0071e3' },
                  },
                }}
              />
            </Box>

            {/* 募集区分 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#3c3c3c', fontSize: '0.875rem' }}>
                募集区分
              </Typography>
              <FormControl fullWidth error={!!fieldErrors.name}>
                <Select
                  name="name"
                  value={currentTemplate?.name || ''}
                  onChange={handleSelectChange}
                  displayEmpty
                  error={!!fieldErrors.name}
                  sx={{
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: fieldErrors.name ? '#d32f2f' : '#e7e7e7' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: fieldErrors.name ? '#d32f2f' : '#c4c4c4' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: fieldErrors.name ? '#d32f2f' : '#0071e3' },
                  }}
                >
                  <MenuItem value="" disabled>
                    <Typography sx={{ color: '#aaa' }}>職種を選択してください</Typography>
                  </MenuItem>
                  {(() => {
                    const grouped = jobPositions.reduce<Record<string, JobPositionWithIndustry[]>>((acc, pos) => {
                      const industryName = pos.industry?.name || '未分類';
                      if (!acc[industryName]) acc[industryName] = [];
                      acc[industryName].push(pos);
                      return acc;
                    }, {});
                    const options = Object.entries(grouped).flatMap(([industryName, positions]) => [
                      <ListSubheader key={`header-${industryName}`} sx={{ bgcolor: '#f5f5f5', fontWeight: 700, color: '#555', lineHeight: '36px' }}>
                        {industryName}
                      </ListSubheader>,
                      ...positions.map(pos => (
                        <MenuItem key={pos.id} value={pos.name}>
                          {pos.name}
                        </MenuItem>
                      ))
                    ]);

                    // 既存ひな形の募集区分が職種マスタに無い（マスタ変更・無効化・取得失敗）場合、
                    // 選択肢と一致せず「未選択」に見えてしまう。編集時に元の値が消えないよう
                    // 現在値を選択肢として補う（動作確認NG: 2 求人作成とLINE配信 ①）
                    const currentName = currentTemplate?.name;
                    if (currentName && !jobPositions.some(pos => pos.name === currentName)) {
                      options.unshift(
                        <MenuItem key={`current-${currentName}`} value={currentName}>
                          {currentName}（現在の設定）
                        </MenuItem>
                      );
                    }

                    return options;
                  })()}
                </Select>
                {fieldErrors.name && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
                    {fieldErrors.name}
                  </Typography>
                )}
              </FormControl>
            </Box>

            {/* ランク */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#3c3c3c', fontSize: '0.875rem' }}>
                ランク
              </Typography>
              <Select
                name="tier_code"
                value={currentTemplate?.tier_code || ''}
                onChange={(e) => {
                  if (!currentTemplate) return;
                  setCurrentTemplate({ ...currentTemplate, tier_code: e.target.value || undefined });
                }}
                displayEmpty
                fullWidth
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '10px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e7e7e7' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c4c4c4' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0071e3' },
                }}
              >
                <MenuItem value="">
                  <Typography sx={{ color: '#aaa' }}>指定なし</Typography>
                </MenuItem>
                {/* 保存は tier_id で行うため、選択肢は店舗のランクマスタから作る。
                    マスタを取得できないときだけ既定コードで代替表示する。 */}
                {(skillTiers.length > 0
                  ? skillTiers.map((t) => ({ code: t.code, label: t.name }))
                  : RANK_TIER_ORDER.map((code) => ({ code, label: RANK_TIER_LABELS[code] }))
                ).map(({ code, label }) => (
                  <MenuItem key={code} value={code}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RankStarInline tierCode={code} size={20} />
                      <Typography>{label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* 募集メモ */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#3c3c3c', fontSize: '0.875rem' }}>
                募集メモ
              </Typography>
              <TextField
                name="admin_label"
                value={currentTemplate?.admin_label || ''}
                onChange={handleInputChange}
                fullWidth
                placeholder="例: 夏季限定、週末シフト用など"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#e7e7e7' },
                    '&:hover fieldset': { borderColor: '#c4c4c4' },
                    '&.Mui-focused fieldset': { borderColor: '#0071e3' },
                  },
                }}
              />
            </Box>

            {/* 雇用形態 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#3c3c3c', fontSize: '0.875rem' }}>
                雇用形態
              </Typography>
              <Select
                name="job_type"
                value={currentTemplate?.job_type || 'パート・アルバイト'}
                onChange={handleSelectChange}
                fullWidth
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '10px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e7e7e7' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c4c4c4' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0071e3' },
                }}
              >
                <MenuItem value="パート・アルバイト">パート・アルバイト</MenuItem>
                <MenuItem value="正社員">正社員</MenuItem>
                <MenuItem value="契約社員">契約社員</MenuItem>
                <MenuItem value="業務委託">業務委託</MenuItem>
                <MenuItem value="派遣社員">派遣社員</MenuItem>
              </Select>
            </Box>

            {/* 時給 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#3c3c3c', fontSize: '0.875rem' }}>
                時給
              </Typography>
              <TextField
                name="salary_amount"
                value={formatWithCommas(currentTemplate?.salary_amount)}
                onChange={handleInputChange}
                fullWidth
                type="text"
                error={!!fieldErrors.salary_amount}
                helperText={fieldErrors.salary_amount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                  inputProps: { inputMode: 'numeric', pattern: '[0-9,]*' },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    '& fieldset': { borderColor: fieldErrors.salary_amount ? '#d32f2f' : '#e7e7e7' },
                    '&:hover fieldset': { borderColor: fieldErrors.salary_amount ? '#d32f2f' : '#c4c4c4' },
                    '&.Mui-focused fieldset': { borderColor: fieldErrors.salary_amount ? '#d32f2f' : '#0071e3' },
                  },
                }}
              />
            </Box>

            {/* 手当 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#3c3c3c', fontSize: '0.875rem' }}>
                手当
              </Typography>
              <TextField
                name="salary_allowance"
                value={formatWithCommas(currentTemplate?.salary_allowance)}
                onChange={handleInputChange}
                fullWidth
                type="text"
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                  inputProps: { inputMode: 'numeric', pattern: '[0-9,]*' },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#e7e7e7' },
                    '&:hover fieldset': { borderColor: '#c4c4c4' },
                    '&.Mui-focused fieldset': { borderColor: '#0071e3' },
                  },
                }}
              />
            </Box>

            {/* 交通費 */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#3c3c3c', fontSize: '0.875rem' }}>
                交通費
              </Typography>
              <TextField
                name="salary_transport"
                value={formatWithCommas(currentTemplate?.salary_transport)}
                onChange={handleInputChange}
                fullWidth
                type="text"
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                  inputProps: { inputMode: 'numeric', pattern: '[0-9,]*' },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#e7e7e7' },
                    '&:hover fieldset': { borderColor: '#c4c4c4' },
                    '&.Mui-focused fieldset': { borderColor: '#0071e3' },
                  },
                }}
              />
            </Box>

            {/* 仕事内容(求人用) */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#3c3c3c', fontSize: '0.875rem' }}>
                仕事内容(求人用)
              </Typography>
              <TextField
                name="content"
                value={currentTemplate?.content || ''}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={7}
                placeholder="求職者向けの仕事内容を記載してください。LINE配信やWEB掲載時に使用されます。"
                error={!!fieldErrors.content}
                helperText={fieldErrors.content}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#e7e7e7' },
                    '&:hover fieldset': { borderColor: '#c4c4c4' },
                    '&.Mui-focused fieldset': { borderColor: '#0071e3' },
                  },
                }}
              />
            </Box>

            {/* 業務内容(労働条件通知書) */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#3c3c3c', fontSize: '0.875rem' }}>
                業務内容(労働条件通知書)
              </Typography>
              <TextField
                name="description"
                value={currentTemplate?.description || ''}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={7}
                placeholder="労働条件通知書に記載する詳細な業務内容"
                helperText="※労働条件通知書などの公式書類で使用される業務内容です"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#e7e7e7' },
                    '&:hover fieldset': { borderColor: '#c4c4c4' },
                    '&.Mui-focused fieldset': { borderColor: '#0071e3' },
                  },
                }}
              />
            </Box>

            {/* LINE配信メッセージ */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#3c3c3c', fontSize: '0.875rem' }}>
                LINE配信メッセージ
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', lineHeight: 1.6 }}>
                職種・報酬・日時・募集人数は自動表示されます。配信に追記したいメッセージを入力してください。自動表示含めて15行以上の場合、配信時に表示しきれない場合があります。
              </Typography>
              <TextField
                name="line_message_template"
                value={currentTemplate?.line_message_template || ''}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={7}
                placeholder="LINE配信時に使用するメッセージを記載してください。"
                error={!!fieldErrors.line_message_template}
                helperText={fieldErrors.line_message_template || '※今すぐ募集で配信されるメッセージです'}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    '& fieldset': { borderColor: '#e7e7e7' },
                    '&:hover fieldset': { borderColor: '#c4c4c4' },
                    '&.Mui-focused fieldset': { borderColor: '#0071e3' },
                  },
                }}
              />
            </Box>

            {/* 有効チェック（右寄せ） */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentTemplate?.is_active ?? true}
                    onChange={(e) => handleSwitchChange('is_active', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#00a29a' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#00a29a' },
                    }}
                  />
                }
                label="有効"
                labelPlacement="start"
              />
            </Box>

            {/* 作成するボタン */}
            <Button
              fullWidth
              onClick={handleSaveTemplate}
              disabled={isLoading || loadingStoreInfo || !storeId}
              sx={{
                bgcolor: '#0071e3',
                color: '#fff',
                borderRadius: '8px',
                py: 1.4,
                fontWeight: 700,
                fontSize: '0.95rem',
                boxShadow: '0 0 10px rgba(4,0,0,0.2)',
                '&:hover': { bgcolor: '#0062c4', boxShadow: '0 0 14px rgba(4,0,0,0.25)' },
                '&.Mui-disabled': { bgcolor: '#c4c4c4', color: '#fff', boxShadow: 'none' },
                mb: 1,
              }}
            >
              {isLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : isCopyMode ? 'コピーして作成する' : selectedTemplate ? '変更を保存する' : '作成する'}
            </Button>
          </Box>
        </Box>
      </Box>
      )}

      {/* テンプレートプレビューモーダル */}
      <Dialog
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          ひな形プレビュー: {selectedTemplate?.template_name || selectedTemplate?.name}
        </DialogTitle>
        <DialogContent dividers>
          {selectedTemplate && (
            <>
              <Card sx={{ mb: 3, bgcolor: 'rgb(240, 248, 255)' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" fontWeight="bold">職種:</Typography>
                      <Typography variant="body2">{selectedTemplate.job_title}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" fontWeight="bold">雇用形態:</Typography>
                      <Typography variant="body2">{selectedTemplate.job_type}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" fontWeight="bold">勤務地:</Typography>
                      <Typography variant="body2">{selectedTemplate.address}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" fontWeight="bold">給与:</Typography>
                      <Typography variant="body2">
                        {formatSalary(selectedTemplate)}
                      </Typography>
                    </Grid>
                    {selectedTemplate.admin_label && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <LabelIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            募集メモ: {selectedTemplate.admin_label}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
              
              {selectedTemplate.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>業務内容（労働条件通知書用）:</Typography>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'rgb(248, 250, 252)', 
                    border: 1, 
                    borderColor: 'grey.300', 
                    borderRadius: 1,
                    whiteSpace: 'pre-wrap' 
                  }}>
                    {selectedTemplate.description}
                  </Box>
                </Box>
              )}
              
              {selectedTemplate.content && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>仕事内容（求人用）:</Typography>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'rgb(255, 248, 230)', 
                    border: 1, 
                    borderColor: 'orange.200', 
                    borderRadius: 1,
                    whiteSpace: 'pre-wrap' 
                  }}>
                    {selectedTemplate.content}
                  </Box>
                </Box>
              )}
              
              {selectedTemplate.line_message_template && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>配信メッセージ:</Typography>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'rgb(240, 248, 255)', 
                    border: 1, 
                    borderColor: 'blue.200', 
                    borderRadius: 1,
                    whiteSpace: 'pre-wrap' 
                  }}>
                    {selectedTemplate.line_message_template}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPreviewOpen(false)} color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* テンプレート詳細表示モーダル */}
      <Dialog
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: isMobile
            ? {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxWidth: 'calc(100% - 24px)',
                maxHeight: 'calc(100dvh - 24px)',
                borderRadius: 2,
              }
            : {},
        }}
      >
        <DialogTitle>
          ひな形詳細: {selectedTemplate?.template_name || selectedTemplate?.name}
          <IconButton
            aria-label="close"
            onClick={() => setIsDetailOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTemplate && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      基本情報
                    </Typography>

                    {selectedTemplate.template_name && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          ひな形名
                        </Typography>
                        <Typography variant="body1">
                          {selectedTemplate.template_name}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        職種
                      </Typography>
                      <Typography variant="body1">
                        {selectedTemplate.job_title}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        雇用形態
                      </Typography>
                      <Typography variant="body1">
                        {selectedTemplate.job_type}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        勤務地
                      </Typography>
                      <Typography variant="body1">
                        {selectedTemplate.address}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        給与
                      </Typography>
                      <Typography variant="body1">
                        {formatSalary(selectedTemplate)}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        状態
                      </Typography>
                      <Chip
                        label={selectedTemplate.is_active ? '有効' : '無効'}
                        color={selectedTemplate.is_active ? 'success' : 'default'}
                        size="small"
                        sx={selectedTemplate.is_active ? { bgcolor: 'rgb(0, 122, 255)' } : {}}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      メタ情報
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        作成日時
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedTemplate.created_at)}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        最終更新日時
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedTemplate.updated_at)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {selectedTemplate.description && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        業務内容（労働条件通知書用）
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'rgb(248, 250, 252)',
                          border: 1,
                          borderColor: 'grey.300',
                          borderRadius: 1,
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {selectedTemplate.description}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {selectedTemplate.content && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        仕事内容（求人用）
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'rgb(255, 248, 230)',
                          border: 1,
                          borderColor: 'orange.200',
                          borderRadius: 1,
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {selectedTemplate.content}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {selectedTemplate.line_message_template && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        配信メッセージ
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'rgb(240, 248, 255)',
                          border: 1,
                          borderColor: 'blue.200',
                          borderRadius: 1,
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {selectedTemplate.line_message_template}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleEditTemplate(selectedTemplate!)}
            startIcon={<EditIcon />}
            variant="outlined"
            sx={{ mr: 'auto' }}
          >
            編集
          </Button>
          <Button
            onClick={() => handleCopyTemplate(selectedTemplate!)}
            startIcon={<ContentCopyIcon />}
            variant="outlined"
            color="secondary"
            sx={{ mr: 1 }}
          >
            コピー
          </Button>
          <Button
            onClick={() => setIsDetailOpen(false)}
            color="primary"
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
      >
        <DialogTitle>
          ひな形を削除
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            「{templateToDelete?.name}」を削除してもよろしいですか？この操作は元に戻せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteAlertOpen(false)} color="inherit">
            キャンセル
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobPostingTemplates;